







































import { computed, defineComponent, PropType, ref, watch } from "@vue/composition-api";
import { isEqual } from "lodash-es";
import { TagInfo } from "@scrile/api-provider/dist/api/TagsProvider";
import { UserLocation } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { BaseMenuItem } from "@/types";
import { sortFields } from "@/const";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseMenu from "@/components/BaseMenu";
import DiscoverFiltersModal from "../DiscoverFiltersModal";

export interface FiltersData {
  tags: TagInfo[];
  hashtags: TagInfo[];
  locations: UserLocation[];
  languages: string[];
}

export default defineComponent({
  name: "DiscoverFilters",
  components: {
    BaseMenu,
    BaseIconButton,
    BaseIcon,
    DiscoverFiltersModal,
  },
  props: {
    selectedTags: { type: Array as () => TagInfo[], required: true },
    tagIds: { type: Array as PropType<string[]>, default: () => [] },
    hashtagIds: { type: Array as PropType<string[]>, default: () => [] },
    languages: { type: Array as PropType<string[]>, default: () => [] },
    locations: { type: Array as PropType<UserLocation[]>, default: () => [] },
    selectedSort: { type: String, default: "newest" },
    // open the filters (hashtag search) right away, used by the "Search" tab
    openFilters: { type: Boolean, default: false },
  },
  setup(props, { root }) {
    const { authUser } = useAuthUser();
    const showFiltersModal = ref(props.openFilters);
    // "Search" tab clicked again on the search page with a hashtag selected
    watch(
      () => props.openFilters,
      open => open && (showFiltersModal.value = true)
    );

    const searchData = computed<FiltersData>(() => ({
      tags: props.selectedTags.filter(i => props.tagIds.includes(i.id)),
      hashtags: props.selectedTags.filter(i => props.hashtagIds.includes(i.id)),
      locations: props.locations,
      languages: props.languages,
    }));

    const allowSearch = computed(() => config.featureSearchBar && !config.featureDiscoveryCreators);
    const allowFilters = computed(
      () => config.featureTags || config.featureHashtags || !config.featureHideLocalization
    );

    const hasActiveFilters = computed(
      () => !!(props.tagIds.length || props.hashtagIds.length || props.locations.length || props.languages.length)
    );
    const sortItems = computed<BaseMenuItem[]>(() =>
      sortFields.map(i => ({
        text: i.title,
        name: i.slug,
        class: i.slug === props.selectedSort ? "fw-600" : "",
      }))
    );
    const selectedSortTitle = computed(() => sortFields.find(item => item.slug === props.selectedSort)?.title);

    const activeFilterName = computed(() => {
      return props.selectedTags.length === 0
        ? selectedSortTitle.value
        : props.selectedTags.length === 1
        ? `${selectedSortTitle.value}, ${props.selectedTags[0].title}`
        : `${selectedSortTitle.value}, ${$t("{0} categories", [props.selectedTags.length])}`;
    });

    const updateRoute = async (data: FiltersData, sort: string) => {
      await root.$router.push({
        name: root.$route.name || "discover",
        query: {
          sort: sort !== "random" ? sort : undefined,
          tag: data.tags?.map(i => i.slug),
          hashtag: data.hashtags?.map(i => i.slug),
          language: data.languages,
          location: data.locations?.map(i => JSON.stringify(i)),
        },
      });
    };

    const onSortSelect = async (sort: string) => {
      if (props.selectedSort === sort) return;
      await updateRoute(searchData.value, sort);
    };

    const onUpdateFilterData = async (data: FiltersData) => {
      showFiltersModal.value = false;
      if (isEqual(searchData.value, data)) return;
      await updateRoute(data, props.selectedSort);
    };

    return {
      authUser,
      showFiltersModal,
      allowSearch,
      allowFilters,
      activeFilterName,
      sortItems,
      hasActiveFilters,
      onUpdateFilterData,
      onSortSelect,
    };
  },
});
